/**
 * Ontology-kind step body for CreateObjectTypeWizard — the required "what is
 * this?" step. Extracted from CreateObjectTypeWizard.tsx to keep that file
 * within the project's file-size convention.
 *
 * This is the only place a customer states the object's ontology kind, and it
 * is required because there is no sane default: a type stamped with no
 * ontology kind is excluded from the Snowflake export and matches no
 * kind-based association endpoint (see `lib/crm/core/ontologyKinds.ts`).
 * Guessing on the customer's behalf would bake a wrong answer into the schema.
 */

import { memo, useCallback, useId } from 'react';

import { FormField } from '@/lib/base/forms/form-field';
import { RadioCard, RadioGroup } from '@/lib/base/radio-buttons/radio-buttons';
import { useWizardContext } from '@/lib/base/wizard';
import type { OntologyKind } from '@/lib/crm/core/ontologyKinds';
import { ONTOLOGY_KIND_OPTIONS } from '@/lib/crm/core/ontologyKinds';

export interface OntologyKindStepProps {
  ontologyKind: OntologyKind | null;
  onOntologyKindChange: (value: OntologyKind) => void;
}

function OntologyKindStepBodyImpl({
  ontologyKind,
  onOntologyKindChange,
}: OntologyKindStepProps) {
  const { isSubmitting } = useWizardContext();
  const errorId = useId();
  const handleChange = useCallback(
    (value: string) => onOntologyKindChange(value as OntologyKind),
    [onOntologyKindChange]
  );

  return (
    <FormField name='ontologyKind' isRequired errorId={errorId}>
      {({ isInvalid, showError }) => (
        <RadioGroup
          value={ontologyKind ?? ''}
          onChange={handleChange}
          isDisabled={isSubmitting}
          isRequired
          isInvalid={isInvalid}
          aria-label='What kind of thing is this?'
          aria-describedby={showError ? errorId : undefined}
          className='flex flex-col gap-3'
        >
          {ONTOLOGY_KIND_OPTIONS.map(option => (
            <RadioCard key={option.value} value={option.value} showIndicator>
              <span className='text-sm font-medium text-[var(--color-text-primary)]'>
                {option.label}
              </span>
              <span className='text-sm text-[var(--color-text-secondary)]'>
                {option.hint}
              </span>
            </RadioCard>
          ))}
        </RadioGroup>
      )}
    </FormField>
  );
}

export const OntologyKindStepBody = memo(OntologyKindStepBodyImpl);
